import { useEffect, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { fetchCatalog } from "../api.js";
import Breadcrumbs from "../components/Breadcrumbs.jsx";
import CatalogItemCard from "../components/CatalogItemCard.jsx";
import { buildIndex, flattenCatalog, searchCatalog } from "../lib/catalogSearch.js";

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = (searchParams.get("q") ?? "").trim();
  const { page } = useOutletContext() ?? {};

  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const groups = await fetchCatalog();
        if (cancelled) return;
        setIndex(buildIndex(flattenCatalog(groups)));
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const results = useMemo(
    () => (index ? searchCatalog(index, query) : []),
    [index, query]
  );

  useEffect(() => {
    const base = page?.title ? page.title.split("—")[0].trim() : "PMP";
    document.title = query ? `Search: ${query} · ${base}` : `Search · ${base}`;
    return () => {
      if (page?.title) document.title = page.title;
    };
  }, [query, page?.title]);

  return (
    <section className="py-5 pmp-section-alt flex-grow-1">
      <Container>
        <Breadcrumbs
          className="mb-3"
          items={[
            { label: "Catalog", to: "/catalog" },
            { label: "Search" },
          ]}
        />
        <p className="pmp-kicker mb-2">Search</p>
        <h1 className="h2 mb-4">
          {query ? (
            <>
              Results for “{query}”
              {!loading && !error ? (
                <span className="text-secondary fs-5 fw-normal ms-2">
                  ({results.length})
                </span>
              ) : null}
            </>
          ) : (
            "Search the catalog"
          )}
        </h1>

        {loading ? (
          <div className="py-5 d-flex justify-content-center">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading</span>
            </Spinner>
          </div>
        ) : error ? (
          <Alert variant="danger" className="shadow-sm">
            {error}
          </Alert>
        ) : !query ? (
          <p className="text-secondary">
            Type a product name, SKU, or keyword in the search box above.
          </p>
        ) : results.length ? (
          <Row className="g-4">
            {results.map((item) => (
              <Col key={item.sku} xs={12} sm={6} lg={4}>
                <CatalogItemCard item={item} />
              </Col>
            ))}
          </Row>
        ) : (
          <div className="pmp-search-noresults">
            <h2 className="h5 mb-2">No products match “{query}”.</h2>
            <p className="text-secondary mb-4">
              Try a different term or browse the full catalog — and if you can’t
              find it, request a quote, since we source beyond what’s listed.
            </p>
            <div className="d-flex flex-wrap gap-2">
              <Link to="/catalog" className="btn btn-primary">
                Browse catalog
              </Link>
              <Link to="/contacts" className="btn btn-outline-primary">
                Request a quote
              </Link>
            </div>
          </div>
        )}
      </Container>
    </section>
  );
}
